import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import IpBlockGuard from "@/components/IpBlockGuard";
import CategoryGateway from "./pages/CategoryGateway";

// Lazy load all routes except the gateway for code splitting
const Index = lazy(() => import("./pages/Index"));
const MatchPage = lazy(() => import("./pages/MatchPage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const Profile = lazy(() => import("./pages/Profile"));
const DMCA = lazy(() => import("./pages/DMCA"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Contact = lazy(() => import("./pages/Contact"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const About = lazy(() => import("./pages/About"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const ChannelSetup = lazy(() => import("./pages/admin/ChannelSetup"));
const StreamerDashboard = lazy(() => import("./pages/streamer/StreamerDashboard"));
const AffiliateDashboard = lazy(() => import("./pages/affiliate/AffiliateDashboard"));
const ModeratorDashboard = lazy(() => import("./pages/moderator/ModeratorDashboard"));
const ApplyRole = lazy(() => import("./pages/ApplyRole"));
const Join = lazy(() => import("./pages/Join"));

const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <IpBlockGuard>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<CategoryGateway />} />
                  <Route path="/home" element={<Index />} />
                  <Route path="/match/:id" element={<MatchPage />} />
                  <Route path="/category/:slug" element={<CategoryPage />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/dmca" element={<DMCA />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/disclaimer" element={<Disclaimer />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/cs" element={<ChannelSetup />} />
                  <Route path="/streamer" element={<StreamerDashboard />} />
                  <Route path="/affiliate" element={<AffiliateDashboard />} />
                  <Route path="/moderator" element={<ModeratorDashboard />} />
                  <Route path="/apply" element={<ApplyRole />} />
                  <Route path="/join" element={<Join />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </IpBlockGuard>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
